import React, { useState, useMemo, useRef } from "react";
import styled from "styled-components";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faArrowUp, faArrowDown } from "@fortawesome/free-solid-svg-icons";
import { ScrollContainer } from "lib/components/ScrollContainer";
import { TableComponent } from "./TableComponent";
import { Input } from "lib/components/Input";

export const Rules = ({ data }) => {
  const [customerFilter, setCustomerFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'rowCount', direction: 'desc' });

  const defaultSortDirections = {
    successful: 'desc',
    foundData: 'desc',
    rowCount: 'desc',
    customer: 'asc',
  };

  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];

    return data.filter((item) =>
      item.item.customer.name.toLowerCase().includes(customerFilter.toLowerCase())
    );
  }, [data, customerFilter]);

  const handleFilterChange = (event) => {
    setCustomerFilter(event.target.value);
  };
  
  const handleSort = (field) => {
    const isSameField = sortConfig.key === field;
    const direction = isSameField
      ? (sortConfig.direction === 'asc' ? 'desc' : 'asc')
      : (defaultSortDirections[field] || 'asc');
  
    setSortConfig({ key: field, direction });
  };
  
  const getSortValue = (item, key) => {
    const isSuccessful = item.result.tables[0].columns.length > 0;
    const hasData = item.result.tables[0].rows[0].length > 0 && isSuccessful;
    
    switch (key) {
      case 'customer':
        return item.customer.name;
      case 'successful':
        return isSuccessful;
      case 'foundData':
        return hasData;
      case 'rowCount':
        return isSuccessful ? item.originalLength : 0;
      default:
        return item.customer.name;
    }
  };
  
  const sortedData = useMemo(() => {
    const sortableData = [...filteredData];
  
    sortableData.sort((a, b) => {
      const aValue = getSortValue(a.item, sortConfig.key);
      const bValue = getSortValue(b.item, sortConfig.key);
    
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    
      // Fallback case: sort by customer name alphabetically when sort on other fields are equivalent
      const aFallback = getSortValue(a.item, 'customer');
      const bFallback = getSortValue(b.item, 'customer');
    
      if (aFallback < bFallback) return -1;
      if (aFallback > bFallback) return 1;
      return 0;
    });
  
    return sortableData;
  }, [filteredData, sortConfig]);

  if (!data) return null;

  return (
    <StyledRulesContainer scrollX="auto" scrollY="unset">
      
      <div style={{ padding: '1rem' }}>
        <Input
          type="text"
          value={customerFilter}
          onChange={handleFilterChange}
          placeholder="Filter by Customer"
          style={{ padding: '0.5rem', width: '200px' }}
        />
      </div>
      <header>
        <div className="small" />
        <button
          className="large"
          onClick={() => handleSort('customer')}
          aria-pressed={sortConfig.key === 'customer'}
          aria-label="Sort by Customer"
        >
          Customer
          <SortIndicator isActive={sortConfig.key === "customer"} direction={sortConfig.direction} />
        </button>
        <button
          className="smaller"
          onClick={() => handleSort('successful')}
          aria-pressed={sortConfig.key === 'successful'}
          aria-label="Sort by Successful"
        >
          Successful
          <SortIndicator isActive={sortConfig.key === "successful"} direction={sortConfig.direction} />
        </button>
        <button
          className="smaller"
          onClick={() => handleSort('foundData')}
          aria-pressed={sortConfig.key === 'foundData'}
          aria-label="Sort by Found data"
        >
          Found data
          <SortIndicator isActive={sortConfig.key === "foundData"} direction={sortConfig.direction} />
        </button>
        <button
          className="smaller"
          onClick={() => handleSort('rowCount')}
          aria-pressed={sortConfig.key === 'rowCount'}
          aria-label="Sort by Row count"
        >
          Row count
          <SortIndicator isActive={sortConfig.key === "rowCount"} direction={sortConfig.direction} />
        </button>
      </header>

      {sortedData.length > 0 ? (
        sortedData.map((result, i) => (
          <Rule
            key={result.item.customer.name}
            {...result}
            isSuccessful={result.item.result.tables[0].columns.length <= 0 ? false : true}
          />
        ))
      ) : (
        <p>No rules ran against this object</p>
      )}
    </StyledRulesContainer>
  );
};

const Rule = ({ item, isSuccessful }) => {
  let hasData;
  if (isSuccessful) {
    hasData = item.result.tables[0].rows[0].length > 0 && item.result.tables[0].columns.length > 0 ? true : false;
  } else {
    hasData = false;
  }

  const [isOpen, setIsOpen] = useState(false);
  const [height, setHeight] = useState(0);
  const contentRef = useRef(null);

  const handleToggle = () => {
    setHeight(isOpen ? 0 : contentRef.current.clientHeight);
    setIsOpen(!isOpen);
  };

  let parsedData = item;
  try {
    parsedData = JSON.parse(item);
  } catch (error) {}
  return (
    <StyledRule open={isOpen} height={height}>
      <rule-header onClick={handleToggle}>
        <div className="small">
          <FontAwesomeIcon icon={faChevronDown} className="arrow" />
        </div>
        <div className="large">{parsedData.customer.name}</div>
        <div className="smaller">
          <Ball color={isSuccessful ? "green" : "red"} />
        </div>
        <div className="smaller">
          <Ball color={hasData ? "green" : "red"} />
        </div>
        <div className="smaller">{isSuccessful && hasData ? item.originalLength : "N/A"}</div>
      </rule-header>
      <rule-body>
        <rule-content ref={contentRef}>
          <TableComponent data={parsedData} />
        </rule-content>
      </rule-body>
    </StyledRule>
  );
};

const Ball = ({ color }) => {
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
      <div style={{ height: "10px", width: "10px", borderRadius: "5px", background: color }} />
    </div>
  );
};

const SortIndicator = ({ isActive, direction }) => (
  <span style={{ marginLeft: "6px" }}>
    <FontAwesomeIcon 
      icon={faArrowUp} 
      style={{ opacity: isActive && direction === "asc" ? 1 : 0.3, fontSize: "10px" }} 
    />
    <FontAwesomeIcon 
      icon={faArrowDown} 
      style={{ opacity: isActive && direction === "desc" ? 1 : 0.3, marginLeft: "2px", fontSize: "10px" }} 
    />
  </span>
);

const StyledRulesContainer = styled(ScrollContainer)`
  margin: -1.8rem;
  margin-top: 3%;
  width: calc(100% + 3.6rem);
  p {
    font-size: 1.5rem;
    color: ${(props) => props.theme.text};
    white-space: pre-wrap;
  }
  & > p {
    text-align: center;
  }
  header {
    font-size: 1.25rem;
    display: flex;
    width: 100%;
    & > div {
      display: flex;
      align-items: center;
      padding: 1.25rem 0.8rem;
      color: ${(props) => props.theme.mediumText};
    }
  }
  .small {
    min-width: 20px;
    max-width: 20px;
    display: flex;
    justify-content: center;
  }
  .smaller {
    min-width: 85px;
    max-width: 120px;
    overflow: unset;
    cursor: pointer;
    background: none;  
    font: inherit;
    border: none;      
    padding: 10px;      
    cursor: pointer;       
    color: ${(props) => props.theme.mediumText};   
    align-items: center;
  }
  .medium {
    min-width: 20%;
    max-width: 120px;
    padding: 0 0.8rem;
    display: flex;
    align-items: center;
    min-height: 4rem;
    max-height: 4rem;
    overflow: unset;
    .tooltip {
      margin-left: 1rem;
    }
  }
  .large {
    flex: 1;
    min-width: 40%;
    cursor: pointer;
    background: none;  
    font: inherit;
    border: none;         
    cursor: pointer;         
    color: ${(props) => props.theme.mediumText};   
    display: flex;      
    align-items: center;
  }
  .center {
    text-align: center;
  }
`;

const StyledRule = styled.div`
  &:nth-child(even) rule-header > div {
    background: ${(props) => props.theme.accentCardBackground};
  }
  rule-header {
    cursor: pointer;
    display: flex;
    align-items: stretch;
    color: ${(props) => props.theme.mediumText};
    font-size: 1.45rem;
    & > div {
      padding: 1.25rem 0.8rem;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  rule-body {
    transition: height 0.3s;
    height: ${(props) => `${props.height}px`};
    overflow: hidden;
    display: block;
    box-shadow: inset 0 0 10px 1px rgba(0, 0, 0, 0.2);
  }

  rule-content {
    display: block;
    margin-left: calc(20px + (3 * 0.8rem) - 1.25rem);
    padding: 1.25rem;
  }

  .arrow {
    transition: transform 0.3s;
    transform: ${(props) => (props.open ? "rotate(180deg)" : "rotate(360deg)")};
  }
`;
